/**
 * Simple API client for HTTP requests
 */

interface ApiOptions extends RequestInit {
  data?: any;
}

class ApiClient {
  private getAuthToken(): string | null {
    const authToken = localStorage.getItem('auth_token');
    return authToken;
  }

  private async request(url: string, options: ApiOptions = {}): Promise<any> {
    const { data, ...fetchOptions } = options;

    const config: RequestInit = {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    };

    // Add auth token if available
    const token = this.getAuthToken();
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
      };
    }

    // Add body if data is provided
    if (data) {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text();
  }

  async get(url: string, options?: ApiOptions): Promise<any> {
    return this.request(url, { ...options, method: 'GET' });
  }

  async post(url: string, data?: any, options?: ApiOptions): Promise<any> {
    return this.request(url, { ...options, method: 'POST', data });
  }

  async put(url: string, data?: any, options?: ApiOptions): Promise<any> {
    return this.request(url, { ...options, method: 'PUT', data });
  }

  async patch(url: string, data?: any, options?: ApiOptions): Promise<any> {
    return this.request(url, { ...options, method: 'PATCH', data });
  }

  async delete(url: string, options?: ApiOptions): Promise<any> {
    return this.request(url, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient();